import React, { useEffect, useState } from "react";
import { Breadcrumb, Select } from "antd";
import { DoubleRightOutlined, ReloadOutlined } from "@ant-design/icons";
import Navbar from "../../components/Navbar/Navbar";
import AssetsCard from "./accountingComponents/assetCard";
import LiabilitiesCard from "./accountingComponents/liabilitiesCard";
import ExpenseCard from "./accountingComponents/expenseCard";
import ProfitLossCard from "./accountingComponents/profitLossCard";
import pie_chart_icon from "../../assets/accounting/pie_chart_icon.svg";
import profit_icon from "../../assets/accounting/profit_icon.svg";
import {
  getAccountingOverview,
  getAccountingProfitLoss,
} from "../../API  Functions/ExpenseManagementFunctions";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
// import ShimmerLoader from "../../loading";
import i18next from "i18next";

import { css } from "@emotion/react";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import { Grid } from "@mui/material";
import dayjs from "dayjs";
import DateFilter from "../../components/ui/DateFilter";

const ShimmerLoader = ({ type }) => {
  if (type === "overview") {
    // Mimic Assets + Liabilities + Balance boxes
    return (
      <div className="grid grid-cols-12 gap-4 mt-4">
        {/* Assets Card Placeholder */}
        <div className="col-span-12 md:col-span-6">
          <Skeleton
            variant="rounded"
            width="100%"
            height={400}
            animation="wave"
          />
        </div>

        {/* Liabilities + Balance Summary */}
        <div className="col-span-12 md:col-span-6 flex flex-col gap-4">
          <Skeleton
            variant="rounded"
            width="100%"
            height={265}
            animation="wave"
          />
          <Skeleton
            variant="rounded"
            width="100%"
            height={120}
            animation="wave"
          />
        </div>
      </div>
    );
  }

  if (type === "profitLoss") {
    // Mimic Profit & Loss cards (two big boxes side by side / stacked on mobile)
    return (
      <div className="grid grid-cols-12 gap-4 mt-4">
        <div className="col-span-12 md:col-span-6">
          <Skeleton
            variant="rounded"
            width="100%"
            height={400}
            animation="wave"
          />
        </div>
        <div className="col-span-12 md:col-span-6">
          <Skeleton
            variant="rounded"
            width="100%"
            height={400}
            animation="wave"
          />
        </div>
      </div>
    );
  }

  return null;
};

const BalanceSummary = ({ assetsTotal, liabilitiesTotal }) => {
  const { t } = useTranslation();
  const balance = assetsTotal - liabilitiesTotal;

  return (
    <div className="col-span-12 md:col-span-12 bg-gradient-to-r from-[#E3F0F9] to-[#E7F2EB] rounded-xl px-6 py-5 mt-2 flex justify-between items-center shadow-sm">
      <div className="flex flex-col">
        <span className="text-sm text-gray-500">{t("Total Asset")}</span>
        <span className="text-lg font-semibold text-gray-900">
          EGP {assetsTotal.toLocaleString()}
        </span>
      </div>
      <div className="flex flex-col border-l border-gray-300 px-6">
        <span className="text-sm text-gray-500">{t("Total Liabilities")}</span>
        <span className="text-lg font-semibold text-gray-900">
          EGP {liabilitiesTotal.toLocaleString()}
        </span>
      </div>
      <div className="flex flex-col border-l border-gray-300 pl-6">
        <span className="text-sm text-black">{t("Balance")}</span>
        <span className="text-lg font-bold text-[#7EC242]">
          EGP {balance.toLocaleString()}
        </span>
      </div>
    </div>
  );
};

const AccountingDashboard = () => {
  const { t } = useTranslation();
  const [assetsData, setAssetsData] = useState([]);
  const [liabilitiesData, setLiabilitiesData] = useState([]);
  const [profitLossData, setProfitLossData] = useState([]);
  const [expenseData, setExpenseData] = useState([]);

  const [loadingOverview, setLoadingOverview] = useState(false);
  const [loadingProfitLoss, setLoadingProfitLoss] = useState(false);

  const company_id = useSelector(
    (state) => state?.login?.user?.result?.company_id,
  );

  const [capitalYear, setCapitalYear] = useState(new Date().getFullYear());
  const [capitalMonth, setCapitalMonth] = useState(new Date().getMonth() + 1);
  const [plYear, setPlYear] = useState(new Date().getFullYear());
  const [plMonth, setPlMonth] = useState(new Date().getMonth() + 1);
  const [selectedRange, setSelectedRange] = useState("all");

  const [selectedRange2, setSelectedRange2] = useState("all");

  const today = dayjs();
  const formatToday = today.format("YYYY-MM-DD");
  const startofMonth = today.startOf("month").format("YYYY-MM-DD");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [startDate2, setStartDate2] = useState("");
  const [endDate2, setEndDate2] = useState("");
  const [refreshCapitalKey, setRefreshCapitalKey] = useState(0);
  const [refreshProfitLossKey, setRefreshProfitLossKey] = useState(0);

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getMonthDateRange = (year, month) => {
    const start = new Date(year, month - 1, 1);
    const end = new Date(year, month, 0);
    return {
      from_date: formatDate(start),
      to_date: formatDate(end),
    };
  };

  // Fetch Profit & Loss
  useEffect(() => {
    const { from_date, to_date } = getMonthDateRange(plYear, plMonth);

    const fetchProfitLoss = async () => {
      try {
        setLoadingProfitLoss(true);
       const profitLoss = await getAccountingProfitLoss({
  company_id: company_id,
  ...(startDate2 != '' && { start_date: startDate2 }),
  ...(endDate2  != '' && { end_date: endDate2 }),
});

        setExpenseData(profitLoss);

        setProfitLossData([
          {
            id: "sales_revenue",
            label: t("Sales Revenue"),
            value: profitLoss.sales_revenue || 0,
            color: "#1B4597",
            bg_color: "#FFEFF9",
          },
          {
            id: "cost_of_good_sold",
            label: t("Cost of goods sold"),
            value: profitLoss.cost_of_goods_sold || 0,
            color: "#EF4444",
            bg_color: "#FEF2F2",
          },
          {
            id: "profit_sales",
            label: t("Profit from Sales"),
            value: profitLoss.profit_from_sales || 0,
            color: "#1B4597",
            bg_color: "#EFF6FF",
          },
          {
            id: "profit_percentage",
            label: t("Profit Percentage"),
            value: profitLoss.profit_percent || 0,
            color: "#10B981",
            bg_color: "#F0FDF4",
          },
          {
            id: "net_profit",
            label: t("Net Profit"),
            value: profitLoss.net_profit || 0,
            color: "#1B4597",
            bg_color: "#F0FDF4",
          },
          {
            id: "net_profit_percentage",
            label: t("Net Profit Percentage"),
            value: profitLoss.net_profit_percent || 0,
            color: "#22C55E",
            bg_color: "bg-gradient-to-r from-[#E3F0F9] to-[#E7F2EB]",
          },
        ]);
      } catch (error) {
        console.error("Error fetching profit & loss", error);
      } finally {
        setLoadingProfitLoss(false);
      }
    };

    fetchProfitLoss();
  }, [startDate2, endDate2, company_id, t, refreshProfitLossKey]);

  // Fetch Capital Overview
  useEffect(() => {
    const { from_date, to_date } = getMonthDateRange(capitalYear, capitalMonth);

    const fetchCapitalOverview = async () => {
      try {
        setLoadingOverview(true);
        const overview = await getAccountingOverview({
          company_id: company_id,
           ...(startDate != '' && { from_date: startDate }),
           ...(endDate != '' && { to_date: endDate }),
          // from_date: startDate,
          // to_date: endDate,
          lang: i18next.language === "ar" ? "ar_001" : "en_US",
        });

        const assets = overview.assets || {};

        const colorPalette = [
          { color: "#3DD598", bg_color: "#E6F9F1" }, // teal green
          { color: "#FF9F43", bg_color: "#FFF3E5" }, // orange
          { color: "#FF6B6B", bg_color: "#FFECEC" }, // coral red
          { color: "#4A90E2", bg_color: "#E4F1F7" }, // blue
          { color: "#FF43B9", bg_color: "#FFEFF9" }, // pink
          { color: "#FDD835", bg_color: "#FFF9E1" }, // yellow
          { color: "#6C63FF", bg_color: "#EDEBFF" }, // purple
          { color: "#1B4597", bg_color: "#E3F2FD" }, // navy blue
          { color: "#EF4444", bg_color: "#FCEAEA" }, // bright red
          { color: "#00B894", bg_color: "#DFF6F1" }, // mint green
          { color: "#0984E3", bg_color: "#DCEAFE" }, // bright blue
          { color: "#6C5CE7", bg_color: "#E9E7FF" }, // indigo
          { color: "#D63031", bg_color: "#FFE6E6" }, // ruby red
          { color: "#E17055", bg_color: "#FFEAE0" }, // orange tan
          { color: "#00CEC9", bg_color: "#D4F0F0" }, // cyan
          { color: "#FFEAA7", bg_color: "#FFF8DC" }, // light yellow
          { color: "#FD79A8", bg_color: "#FFE5EF" }, // pink blush
          { color: "#636E72", bg_color: "#F1F3F4" }, // gray
          { color: "#2D3436", bg_color: "#E8E9EB" }, // dark gray
          { color: "#E84393", bg_color: "#FFE3F0" }, // bright pink
        ];

        // Dynamically assign colors from the palette
        const paymentData = Object.entries(assets?.total_payments || {})
          // .filter(([_, value]) => value > 0) // exclude 0 and negative values
          .map(([method, value], index) => {
            const paletteIndex = index % colorPalette.length; // Loop if more methods than colors
            const { color, bg_color } = colorPalette[paletteIndex];

            return {
              id: index + 1,
              value: value,
              label: method,
              color,
              bg_color,
            };
          });

        // Add bank account balances
        const bankAccountsData = (assets?.bank_accounts || []).map(
          (acct, index) => {
            const paletteIndex =
              (paymentData.length + index) % colorPalette.length;
            const { color, bg_color } = colorPalette[paletteIndex];

            return {
              id: 1000 + index,
              value: acct.balance || 0,
              label: acct.bank_name + (acct.is_default ? t(" (Primary)") : ""),
              color,
              bg_color,
            };
          },
        );

        setAssetsData([
          {
            id: 0,
            value: assets?.inventory_value || 0,
            label: t("Inventory Value"),
            color: "#6C63FF",
            bg_color: "#EFF6FF",
          },
          ...paymentData,
          ...bankAccountsData,
          {
            id: 99,
            value: assets?.customer_credit || 0,
            label: t("Credit Dues"),
            color: "#2D3436",
            bg_color: "#E8E9EB",
          },
          {
            id: 999,
            value: assets?.pending_amount || 0,
            label: t("Order Due Amount"),
            color: "#8B0000",
            bg_color: "#FDEDED",
          },
        ]);

        const liabilities = overview.liabilities || {};
        setLiabilitiesData([
          {
            id: "vendor_dues",
            label: t("Vendor Dues"),
            value: liabilities.vendor_dues || 0,
            color: "#1B4597",
            bg_color: "#FEF2F2",
          },
          {
            id: "myginne_dues",
            label: t("myGINNE Dues"),
            value: liabilities.myginne_dues || 0,
            color: "#EF4444",
            bg_color: "#F0F2FD",
          },
        ]);
      } catch (error) {
        console.error("Error fetching capital overview", error);
      } finally {
        setLoadingOverview(false);
      }
    };

    fetchCapitalOverview();
  }, [startDate, endDate, company_id, t, refreshCapitalKey]);

  // Month translation
  const monthOptions = [
    { value: 1, label: t("January") },
    { value: 2, label: t("February") },
    { value: 3, label: t("March") },
    { value: 4, label: t("April") },
    { value: 5, label: t("May") },
    { value: 6, label: t("June") },
    { value: 7, label: t("July") },
    { value: 8, label: t("August") },
    { value: 9, label: t("September") },
    { value: 10, label: t("October") },
    { value: 11, label: t("November") },
    { value: 12, label: t("December") },
  ];

  return (
    <div className="w-full flex flex-col md:h-full overflow-y-auto bg-[#F6F7F9]">
      <div>
        <Navbar heading={t("Accounting Dashboard")} />
      </div>

      <div className="flex-grow overflow-y-auto custom-scrollbar overflow-hidden p-4">
        <div className="flex justify-between mt-6 mb-4">
          <div className="flex items-center text-lg font-semibold">
            <img
              src={pie_chart_icon}
              alt="Capital Overview Icon"
              className="w-10 h-10 me-2"
            />
            {t("Capital Overview")}
          </div>
          <div className="flex gap-3">
            {/* <Select
              value={capitalYear}
              onChange={(val) => setCapitalYear(val)}
              options={Array.from({ length: 6 }, (_, i) => {
                const y = new Date().getFullYear() - i;
                return { value: y, label: y };
              })}
              style={{ width: 120 }}
            />
            <Select
              value={capitalMonth}
              onChange={(val) => setCapitalMonth(val)}
              options={monthOptions}
              style={{ width: 140 }}
            /> */}
            <div className=" mb-2">
              <DateFilter
                setSelectedRange={setSelectedRange}
                selectedRange={selectedRange}
                SubmitFilter={(start, end) => {
                  setStartDate(start);
                  setEndDate(end);
                }}
                isAllTime={true}
              />
            </div>
            <div
              className="flex items-center text-gray-500 cursor-pointer text-sm"
              onClick={() => setRefreshCapitalKey((prev) => prev + 1)}
            >
              <ReloadOutlined />
              <p className=" ms-2">{t("Refresh")}</p>
            </div>
          </div>
        </div>

        {loadingOverview ? (
          <ShimmerLoader type="overview" />
        ) : (
          <div dir={"ltr"} className="grid grid-cols-12 gap-4">
            <AssetsCard
              assetsData={assetsData}
              noDataText={t("No data available")}
            />
            <div className="col-span-12 md:col-span-6 flex flex-col gap-2">
              <LiabilitiesCard liabilitiesData={liabilitiesData} />
              <BalanceSummary
                assetsTotal={assetsData.reduce(
                  (sum, item) => sum + item.value,
                  0,
                )}
                liabilitiesTotal={liabilitiesData.reduce(
                  (sum, item) => sum + item.value,
                  0,
                )}
              />
            </div>
          </div>
        )}

        {/* Profit & Loss */}
        <div className="flex justify-between mt-8">
          <div className="flex items-center text-lg font-semibold">
            <img
              src={profit_icon}
              alt="Profit Icon"
              className="w-12 h-12 me-2"
            />
            {t("Profit & Loss")}
          </div>
          <div className="flex gap-3">
            {/* <Select
              value={plYear}
              onChange={(val) => setPlYear(val)}
              options={Array.from({ length: 6 }, (_, i) => {
                const y = new Date().getFullYear() - i;
                return { value: y, label: y };
              })}
              style={{ width: 120 }}
            />
            <Select
              value={plMonth}
              onChange={(val) => setPlMonth(val)}
              options={monthOptions}
              style={{ width: 140 }}
            /> */}
            <div className=" mb-2">
              <DateFilter
                setSelectedRange={setSelectedRange2}
                selectedRange={selectedRange2}
                SubmitFilter={(start, end) => {
                  setStartDate2(start);
                  setEndDate2(end);
                }}
                isAllTime={true}
              />
            </div>

            <div
              className="flex items-center text-gray-500 cursor-pointer text-sm"
              onClick={() => setRefreshProfitLossKey((prev) => prev + 1)}
            >
              <ReloadOutlined />
              <p className=" ms-2">{t("Refresh")}</p>
            </div>
          </div>
        </div>

        {loadingProfitLoss ? (
          <ShimmerLoader type="profitLoss" />
        ) : (
          <div className="grid grid-cols-12 gap-4 mt-4">
            <div
              dir={i18next.language === "ar" ? "rtl" : "ltr"}
              className="contents"
            >
              <ProfitLossCard
                profitLossData={profitLossData}
                noDataText={t("No data available")}
              />
              <ExpenseCard expenseData={expenseData} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AccountingDashboard;
